import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { patient } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { SickNote } from '@/types/medical';

export function PatientSickNotesPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'sick-notes'],
    queryFn:  patient.sickNotes.list,
  });

  return (
    <>
      <PageHeader title="My sick notes" />
      <DataState
        query={q}
        empty={{
          title: 'No sick notes on record',
          description: 'When your clinician issues a sick note, it appears here.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((s) => <Row key={s.id} note={s} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({ note }: { note: SickNote }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <LocalHospitalIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {note.reason || 'Sick leave'}
              </Typography>
              <Chip
                size="small"
                label={note.status}
                color={note.status === 'registered' || note.status === 'signed' ? 'primary' : 'default'}
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {note.clinician_name} · {note.jurisdiction_code}
              {note.icd_code && ` · ICD ${note.icd_code}`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', minWidth: 200 }}>
            <Typography variant="body2" className="tabular" fontWeight={500}>
              {formatDate(note.valid_from)} – {formatDate(note.valid_to)}
            </Typography>
            {note.signed_at && (
              <Typography variant="caption" color="text.secondary" className="tabular">
                signed {formatDateTime(note.signed_at)}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
